/** The sync engine: poll the change feed and keep the kept accounts fresh (design 16.8, rule U-36). */

import { useSyncExternalStore } from "react";
import * as api from "../api";
import { onBeforeReload } from "../offline/update";
import { accountsExtra, ACCOUNTS_VIEW, COLLECTION, keepAccounts } from "./accounts";
import { dropPastViews } from "./pageViews";
import { applyRows, checkAccess, currentScope, note, readView, scopeMeta, setMeta, setWaker, swapRows, whenOpen } from "./store";
import { markStale, viewsWanted } from "./views";

export const POLL_MS = 10_000;
export const BACKOFF_MS = [10_000, 20_000, 60_000];
export const HIDDEN_STOP_MS = 5 * 60_000;
export const ROWS_BATCH = 500;
/** The most feed pages in one sync; the next sync goes on from the cursor. */
const MAX_PAGES = 20;

export type SyncMode = "unknown" | "feed" | "fallback";
export type SyncFailure = "network" | "server" | "session";

export interface SyncStatus {
  mode: SyncMode;
  /** The client time of the last sync that ended with no error. */
  syncedAt: number | null;
  failed: SyncFailure | null;
}

export interface SyncConfig {
  timeZone: string;
  token: () => Promise<string>;
}

/** The browser seams; the tests change them. */
export const deps = {
  now: () => Date.now(),
  visible: () => document.visibilityState !== "hidden",
  win: (): EventTarget => window,
  doc: (): EventTarget => document,
};

let status: SyncStatus = { mode: "unknown", syncedAt: null, failed: null };
const listeners = new Set<() => void>();
let config: SyncConfig | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
let running: Promise<void> | null = null;
let again: string | null = null;
let errors = 0;
let wanted = false;
let hiddenAt: number | null = null;
let parked = false;
let told = false;
let detach: (() => void) | null = null;
let cleaned: string | null = null;

function setStatus(change: Partial<SyncStatus>) {
  status = { ...status, ...change };
  for (const listener of listeners) listener();
}

export const syncStatus = () => status;

export function subscribeSync(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(subscribeSync, syncStatus, syncStatus);
}

/** A cursor above each head: the feed answers with its head and no events. */
const HEAD_PROBE = Number.MAX_SAFE_INTEGER;
/** The resync reasons that can hide changes of the views; a first load of the accounts cannot. */
const STALE_RESYNC = new Set(["bulk", "behind_floor", "ahead_of_head", "access_key", "day"]);

const entitiesOf = (feed: api.ChangeFeed) => [...new Set(feed.events.map((event) => event.entity))];

/** The org-local date, as YYYY-MM-DD. */
export const dayIn = (timeZone: string, at: number) => new Date(at).toLocaleDateString("en-CA", { timeZone });

const failure = (e: unknown): SyncFailure => {
  const code = e instanceof api.ApiError ? e.status : 0;
  return code === 401 || code === 403 ? "session" : code >= 500 ? "server" : "network";
};

function fallback(why: string) {
  if (status.mode === "fallback") return;
  setMeta({ cursor: null });
  setStatus({ mode: "fallback", failed: null });
  if (!told) note("sync_fallback", "sync: no feed on this API, full poll", { why });
  told = true;
}

/** True when the org or the user changed after the sync started: its rows and cursor belong to the old scope. */
function moved(cfg: SyncConfig, key: string | null): boolean {
  if (config === cfg && currentScope() === key) return false;
  note("sync_dropped", "sync: the scope changed during the sync, its answer is dropped", { why: "scope_change" });
  return true;
}

/** Load all accounts in the background and swap them in; the cursor becomes the as_of of the list. */
async function resync(token: string, reason: string, cfg: SyncConfig, key: string | null): Promise<void> {
  const started = performance.now();
  const at = deps.now();
  const found = await api.accountList(token);
  if (moved(cfg, key)) return;
  if (typeof found.as_of !== "number") {
    keepAccounts(found, at);
    fallback("no_as_of");
    return;
  }
  const swapped = await swapRows(COLLECTION, ACCOUNTS_VIEW, found.accounts, accountsExtra(found), at, found.as_of, { cursor: found.as_of, day: dayIn(cfg.timeZone, at) });
  if (!swapped) return;
  if (reason === "day") dropPastViews(dayIn(cfg.timeZone, at), "day");
  if (STALE_RESYNC.has(reason)) markStale(reason === "day" ? null : [COLLECTION], reason);
  const ms = Math.round(performance.now() - started);
  note("sync_resync", `sync: resync (${reason}), ${found.accounts.length} rows, cursor ${found.as_of}, in ${ms} ms`, { reason, rows: found.accounts.length, ms });
}

/** Read the feed from the cursor, page by page while `more`, and merge the dirty rows. */
async function follow(token: string, since: number, cfg: SyncConfig, key: string | null): Promise<void> {
  let cursor = since;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const started = performance.now();
    const feed = await api.changes(token, cursor);
    if (moved(cfg, key)) return;
    if (await checkAccess(feed.access_key, false)) return resync(token, "access_key", cfg, key);
    markStale(entitiesOf(feed), "feed");
    const whole = feed.events.some((event) => event.entity === COLLECTION && event.ids === null);
    if (feed.resync.includes(COLLECTION) || whole || feed.reason === "behind_floor" || feed.reason === "ahead_of_head") {
      return resync(token, feed.reason ?? "bulk", cfg, key);
    }
    const ids = [...new Set(feed.events.filter((event) => event.entity === COLLECTION).flatMap((event) => event.ids ?? []))];
    let changed = 0;
    let removed = 0;
    for (let start = 0; start < ids.length; start += ROWS_BATCH) {
      const batch = ids.slice(start, start + ROWS_BATCH);
      const found = await api.accountRows(token, batch);
      if (moved(cfg, key)) return;
      const last = start + ROWS_BATCH >= ids.length;
      // The cursor moves to the head only with the last batch, after its rows are on the disk.
      const applied = await applyRows(COLLECTION, ACCOUNTS_VIEW, found.accounts, found.gone, found.as_of, last ? feed.head : cursor);
      if (!applied) return;
      changed += applied.changed;
      removed += applied.removed;
    }
    if (!ids.length) {
      if (feed.head === cursor) return logSync(since, feed.head, 0, 0, 0, 0, performance.now() - started);
      if (moved(cfg, key)) return;
      const applied = await applyRows(COLLECTION, ACCOUNTS_VIEW, [], [], 0, feed.head);
      if (!applied) return;
    }
    logSync(cursor, feed.head, feed.events.length, ids.length, changed, removed, performance.now() - started);
    cursor = feed.head;
    if (!feed.more) return;
  }
  note("sync_paged", `sync: stopped after ${MAX_PAGES} pages, cursor ${cursor}; the next sync goes on`, { pages: MAX_PAGES, cursor });
}

function logSync(since: number, head: number, events: number, ids: number, changed: number, removed: number, took: number) {
  const ms = Math.round(took);
  const lag = status.syncedAt === null ? null : deps.now() - status.syncedAt;
  const text = `sync: since ${since}, head ${head}, ${events} events, ${ids} ids, ${changed} changed, ${removed} gone, in ${ms} ms`;
  if (events || ids) note("sync", text, { since, head, events, ids, changed, removed, lag_ms: lag, ms });
  else console.info(text);
}

/** With no kept accounts, the feed only marks the kept views stale: no account rows are loaded. */
async function followViews(token: string, cfg: SyncConfig, key: string | null): Promise<void> {
  const started = performance.now();
  const meta = scopeMeta();
  let cursor = meta?.cursor ?? null;
  const today = dayIn(cfg.timeZone, deps.now());
  if (cursor === null) {
    const top = await api.changes(token, HEAD_PROBE);
    if (moved(cfg, key)) return;
    await checkAccess(top.access_key, false);
    setMeta({ cursor: top.head, day: today });
    note("sync_views", `sync: views only, cursor ${top.head} from the head`, { head: top.head });
    return;
  }
  if (meta?.day && meta.day !== today) {
    dropPastViews(today, "day");
    markStale(null, "day");
    setMeta({ day: today });
  }
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const feed = await api.changes(token, cursor);
    if (moved(cfg, key)) return;
    if (await checkAccess(feed.access_key, false)) markStale(null, "access_key");
    if (feed.reason === "behind_floor" || feed.reason === "ahead_of_head") markStale(null, feed.reason);
    else markStale([...feed.resync, ...entitiesOf(feed)], feed.resync.length ? "bulk" : "feed");
    setMeta({ cursor: feed.head });
    logSync(cursor, feed.head, feed.events.length, 0, 0, 0, performance.now() - started);
    cursor = feed.head;
    if (!feed.more) return;
  }
}

async function once(reason: string): Promise<void> {
  const cfg = config;
  if (!cfg) return;
  await whenOpen();
  if (currentScope() !== null && cleaned !== currentScope()) {
    cleaned = currentScope();
    dropPastViews(dayIn(cfg.timeZone, deps.now()), "open");
  }
  if (currentScope() === null || status.mode === "fallback") return;
  const kept = readView(ACCOUNTS_VIEW) !== null;
  const views = !wanted && !kept && viewsWanted();
  if (!wanted && !kept && !views) return;
  const key = currentScope();
  const token = await cfg.token();
  if (moved(cfg, key)) return;
  const meta = scopeMeta();
  const cursor = meta?.cursor ?? null;
  try {
    if (views) await followViews(token, cfg, key);
    else if (cursor === null || !kept) await resync(token, cursor === null ? "no_cursor" : "no_rows", cfg, key);
    else if (meta?.day && meta.day !== dayIn(cfg.timeZone, deps.now())) await resync(token, "day", cfg, key);
    else await follow(token, cursor, cfg, key);
  } catch (e) {
    if (e instanceof api.ApiError && e.status === 404) return fallback("no_feed");
    throw e;
  }
  if (config !== cfg) return;
  if (syncStatus().mode !== "fallback") setStatus({ mode: "feed", syncedAt: deps.now(), failed: null });
  if (errors) note("sync_recovered", `sync: back after ${errors} errors (${reason})`, { attempts: errors });
  errors = 0;
}

function schedule(ms: number) {
  clearTimeout(timer);
  timer = undefined;
  if (!config || status.mode === "fallback") return;
  if (!deps.visible() && hiddenAt !== null && deps.now() - hiddenAt >= HIDDEN_STOP_MS) {
    parked = true;
    note("sync_stopped", "sync: stopped, the tab is hidden for 5 min", { hidden_ms: deps.now() - hiddenAt });
    return;
  }
  timer = setTimeout(() => void syncNow("timer"), ms);
}

/** Sync at once; a sync that runs gets one more run after it. */
export function syncNow(reason: string): Promise<void> {
  if (!config) return Promise.resolve();
  if (running) {
    again = reason;
    return running;
  }
  parked = false;
  clearTimeout(timer);
  running = (async () => {
    let next: string | null = reason;
    let delay = POLL_MS;
    while (next !== null) {
      again = null;
      try {
        await once(next);
        delay = POLL_MS;
      } catch (e) {
        errors += 1;
        delay = BACKOFF_MS[Math.min(errors, BACKOFF_MS.length) - 1];
        setStatus({ failed: failure(e) });
        note("sync_retry", `sync: ${failure(e)} error, try ${errors}, next in ${delay / 1_000} s`, { attempt: errors, delay, error: failure(e) });
        again = null;
      }
      next = again;
    }
    running = null;
    schedule(delay);
  })();
  return running;
}

/** The Accounts page opens: sync at once, and keep the kept list fresh from now on. */
export function wantAccounts(): void {
  wanted = true;
  void syncNow("open");
}

/** Start one poller for this tab; it returns the stop function. */
export function startSync(cfg: SyncConfig): () => void {
  stopSync();
  config = cfg;
  hiddenAt = deps.visible() ? null : deps.now();
  const now = (reason: string) => () => void syncNow(reason);
  const focus = now("focus");
  const online = now("online");
  const shown = () => {
    if (!deps.visible()) {
      hiddenAt ??= deps.now();
      return;
    }
    const away = hiddenAt === null ? 0 : deps.now() - hiddenAt;
    hiddenAt = null;
    if (parked) note("sync_started", "sync: started again, the tab shows", { hidden_ms: away });
    void syncNow("visible");
  };
  deps.win().addEventListener("focus", focus);
  deps.win().addEventListener("online", online);
  deps.doc().addEventListener("visibilitychange", shown);
  setWaker((reason) => void syncNow(reason));
  const unhook = onBeforeReload(async () => {
    stopSync();
    await running;
  });
  detach = () => {
    deps.win().removeEventListener("focus", focus);
    deps.win().removeEventListener("online", online);
    deps.doc().removeEventListener("visibilitychange", shown);
    setWaker(null);
    unhook();
  };
  void syncNow("start");
  return stopSync;
}

export function stopSync(): void {
  clearTimeout(timer);
  timer = undefined;
  detach?.();
  detach = null;
  config = null;
}

/** For tests: resolves when the running sync ends. */
export const settled = (): Promise<void> => running ?? Promise.resolve();

/** For tests: forget the state of the poller. */
export function resetForTests(): void {
  stopSync();
  status = { mode: "unknown", syncedAt: null, failed: null };
  running = null;
  again = null;
  errors = 0;
  wanted = false;
  hiddenAt = null;
  parked = false;
  told = false;
  cleaned = null;
}
